import { useState, useEffect, useLayoutEffect, useRef, useCallback, useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { chatMarkdownComponents } from './utils/chatTable';
import { alignNumericColumns } from './utils/markdownTables';
import rehypeRaw from 'rehype-raw';
import './translations';
import './ExpandableAdvisorReasoning.css';
import { useTranslation } from 'react-i18next';
import { API_BASE_URL, getAuthHeaders, appendTokenToUrl, isTokenExpired, notifyUnauthorized, openAuthenticatedFile, isProtectedFileUrl } from './auth';
import AdvisorVoiceDialog from './AdvisorVoiceDialog';
import AreDisclaimer from './AreDisclaimer';
import { chatBubbleSx, chatRowSx, modalHeaderBtnSx, InfoTooltip, FIRST_LINE_SLOT_PX, ScrollToBottomButton, ModalHeader, closeOnlyViaButton, BotAvatar } from './chatMessageStyles';
import useScrollToBottom from './hooks/useScrollToBottom';
import { encodeWAV } from './utils/audioUtils';

import Box from '@mui/material/Box';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import InputAdornment from '@mui/material/InputAdornment';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Collapse from '@mui/material/Collapse';

import SendIcon from '@mui/icons-material/Send';
import MicIcon from '@mui/icons-material/Mic';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import RefreshIcon from '@mui/icons-material/Refresh';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import TravelExploreIcon from '@mui/icons-material/TravelExplore';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import StopCircleIcon from '@mui/icons-material/StopCircle';
import AddIcon from '@mui/icons-material/Add';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
// Category icons (differentiate by shape instead of colour)
import ForumOutlinedIcon from '@mui/icons-material/ForumOutlined';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import AltRouteIcon from '@mui/icons-material/AltRoute';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
// Section + semantic icons
import GavelIcon from '@mui/icons-material/Gavel';
import ListAltIcon from '@mui/icons-material/ListAlt';
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import DescriptionIcon from '@mui/icons-material/Description';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

// Analysis list fields that become deepenable "Posten" cards. Categories are
// distinguished by ICON (not colour) to keep the palette calm — a single blue
// accent is used throughout; the icon carries the category identity.
const POSTEN_CATEGORIES = [
  { field: 'dominant_narratives',   titleKey: 'are_cat_narratives',     Icon: ForumOutlinedIcon },
  { field: 'positive_developments', titleKey: 'are_cat_positive',       Icon: TrendingUpIcon },
  { field: 'negative_developments', titleKey: 'are_cat_negative',       Icon: TrendingDownIcon },
  { field: 'turning_points',        titleKey: 'are_cat_turning',        Icon: AltRouteIcon },
  { field: 'key_uncertainties',     titleKey: 'are_cat_uncertainties',  Icon: HelpOutlineIcon },
];

const ACCENT = '#19d0ef'; // the single accent colour (blue)

// Navigation depth per screen — used to pick the slide direction (forward vs back).
const SCREEN_DEPTH = { assistant: 0, list: 0, free: 0, detail: 1, details: 2 };
// Run statuses that mean "still working" — re-opening one resumes the live view.
const IN_PROGRESS = ['planning', 'planned', 'collecting', 'analyzing'];

function genSessionId(prefix = 'are') {
  return `${prefix}-${Date.now()}-${Math.floor(Math.random() * 100000)}`;
}

function itemLabel(item) {
  if (item == null) return '';
  if (typeof item === 'string') return item;
  return item.label || item.description || item.text || '';
}

function truncate(s, n) {
  s = s || '';
  return s.length > n ? `${s.slice(0, n - 1)}…` : s;
}

// Turn a #rrggbb category colour into an rgba() with the given alpha (for tinted
// tag backgrounds/borders that keep the narrative's colour identity).
function hexA(hex, a) {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex || '');
  if (!m) return hex;
  return `rgba(${parseInt(m[1], 16)}, ${parseInt(m[2], 16)}, ${parseInt(m[3], 16)}, ${a})`;
}

// Format DB timestamps/dates in the user's selected language (Intl resolves the
// regional default per language code). DB values are naive "YYYY-MM-DD HH:MM:SS".
function _toDate(value) {
  if (!value) return null;
  const d = new Date(String(value).replace(' ', 'T'));
  return isNaN(d.getTime()) ? null : d;
}
function formatDateTime(value, language) {
  const d = _toDate(value);
  if (!d) return '';
  try {
    return new Intl.DateTimeFormat(language || 'de', {
      year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit',
    }).format(d);
  } catch (e) { return String(value); }
}
function formatDate(value, language) {
  const d = _toDate(value);
  if (!d) return '';
  try {
    return new Intl.DateTimeFormat(language || 'de', { year: 'numeric', month: '2-digit', day: '2-digit' }).format(d);
  } catch (e) { return String(value); }
}

// Older briefings put the analysis date in the H1 ("… — 2026-06-10"); drop the
// trailing dash + date so the title is just "{Company} Narrativ-Update".
function stripTitleDate(md) {
  if (!md) return md;
  return md.replace(/^(#\s+[^\n]*?)\s+[—–-]\s+[^\n]*$/m, '$1');
}

function ExpandableAdvisorReasoning({ language = 'de', onClose }) {
  const { t } = useTranslation();

  const [tab, setTab] = useState(0); // 0 = assistant, 1 = runs, 2 = Not Controlled Chat
  const [voiceOpen, setVoiceOpen] = useState(false);

  // ── Assistant chat state ──
  const sessionIdRef = useRef(genSessionId());
  const [messages, setMessages] = useState([{ sender: 'bot', text: t('are_assistant_welcome'), welcome: true }]);
  // The language the welcome bubble was written in. A welcome is never rewritten
  // under the advisor — switching the app language must not shift a thread that
  // is already running — so "Clear chat" is how you get one in the new language.
  // Which means the button has to stay reachable while the welcome is stale;
  // see chatTabEmpty below.
  const [welcomeLang, setWelcomeLang] = useState(language);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const botBufRef = useRef('');
  const chatScrollRef = useRef(null);
  // Chat scroll behaviour: user message slides to the top on send, reply streams
  // below, "scroll to latest" arrow when not at the bottom (ported from chatModal).
  const {
    showScrollDown, bottomSpacerRef, messagesEndRef, handleScroll,
    scrollToBottom, markUserSent, syncOnContentChange,
  } = useScrollToBottom(chatScrollRef);

  // ── "Not Controlled Chat" state (third tab) ──
  // A plain ChatGPT-style chat with web search, deliberately outside the ARE
  // pipeline (see src/controller/are/free_chat.py). Everything is kept separate
  // from the assistant thread — own session id, transcript, scroll state — so
  // switching tabs never mixes the two conversations.
  const freeSessionIdRef = useRef(genSessionId('ncc'));
  const [freeMessages, setFreeMessages] = useState([
    { sender: 'bot', text: t('are_free_welcome'), welcome: true },
  ]);
  const [freeWelcomeLang, setFreeWelcomeLang] = useState(language);
  const [freeVoiceOpen, setFreeVoiceOpen] = useState(false);
  const [freeInput, setFreeInput] = useState('');
  const [freeSending, setFreeSending] = useState(false);
  const [freeSearching, setFreeSearching] = useState(false); // web search in flight
  const freeBufRef = useRef('');
  const freeEsRef = useRef(null);   // the live EventSource, so "stop" can close it
  const freeScrollRef = useRef(null);
  const {
    showScrollDown: freeShowScrollDown, bottomSpacerRef: freeBottomSpacerRef,
    messagesEndRef: freeMessagesEndRef, handleScroll: freeHandleScroll,
    scrollToBottom: freeScrollToBottom, markUserSent: freeMarkUserSent,
    syncOnContentChange: freeSyncOnContentChange,
  } = useScrollToBottom(freeScrollRef);

  // ── Speech-to-text (dictation into the input; not the voice agent) ──
  const [isRecording, setIsRecording] = useState(false);
  const [isAudioLoading, setIsAudioLoading] = useState(false);
  const audioContextRef = useRef(null);
  const mediaStreamSourceRef = useRef(null);
  const processorRef = useRef(null);
  const audioBufferRef = useRef([]);
  // Which input the transcript is typed into — both chat tabs share one recorder.
  const sttTargetRef = useRef('assistant'); // 'assistant' | 'free'

  // ── Runs state ──
  const [runs, setRuns] = useState([]);
  const [runsLoading, setRunsLoading] = useState(false);
  const [selectedRun, setSelectedRun] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [running, setRunning] = useState(false);
  const [runPhase, setRunPhase] = useState('');     // 'collecting' | 'analyzing'
  const [runSteps, setRunSteps] = useState([]);     // [{ id, label, status: 'running'|'done'|'not_found', reason? }]
  const [runTotal, setRunTotal] = useState(0);      // total topics planned for this run
  const [runError, setRunError] = useState('');

  // per-Posten deepen progress: { [item_key]: 'progress text' }
  const [deepenProgress, setDeepenProgress] = useState({});
  // argumentation
  const [argLoading, setArgLoading] = useState(false);
  const [argLoadingPos, setArgLoadingPos] = useState(null); // which positioning is being (re)generated
  const [argError, setArgError] = useState('');
  const [selectedPos, setSelectedPos] = useState(null);     // active buy/hold/sell tab
  // Collapsible sections. `openMap[key]` holds an explicit user choice; when
  // unset, the per-section `def` (default open/closed) applies.
  const [openMap, setOpenMap] = useState({});
  const isOpen = (key, def = true) => (openMap[key] === undefined ? def : openMap[key]);
  const toggleSection = (key, def = true) => setOpenMap((m) => ({ ...m, [key]: !(m[key] === undefined ? def : m[key]) }));

  // ── Narrative CRUD state ──
  const [editingKey, setEditingKey] = useState(null);   // item_key being edited
  const [editValue, setEditValue] = useState('');
  const [addingCat, setAddingCat] = useState(null);     // category field with an open add-form
  const [addValue, setAddValue] = useState('');
  const [focusKey, setFocusKey] = useState(null);       // item_key with an open deepen-focus input
  const [focusValue, setFocusValue] = useState('');
  const [crudKey, setCrudKey] = useState(null);         // item_key / `add:<field>` currently saving

  // ── "Mehr Details" page (Themen / Quellen tabs) ──
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [detailsTab, setDetailsTab] = useState(0); // 0 = Themen, 1 = Quellen

  // Track open EventSources so we can close them on unmount.
  const sourcesRef = useRef([]);
  const registerSource = (es) => { sourcesRef.current.push(es); return es; };
  // Polling intervals for an in-flight run, any in-flight "vertiefen", and the
  // background Bericht (re)generation.
  const pollRef = useRef(null);
  const deepenPollsRef = useRef([]);
  const berichtPollRef = useRef(null);
  const prevScreenRef = useRef('assistant'); // last shown screen, for slide direction
  const stopRunWatchers = useCallback(() => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    deepenPollsRef.current.forEach((iv) => clearInterval(iv));
    deepenPollsRef.current = [];
  }, []);
  useEffect(() => () => {
    if (pollRef.current) clearInterval(pollRef.current);
    if (berichtPollRef.current) clearInterval(berichtPollRef.current);
    deepenPollsRef.current.forEach((iv) => clearInterval(iv));
    sourcesRef.current.forEach((es) => { try { es.close(); } catch (e) {} });
  }, []);

  // Position the just-sent user message at the top of the viewport, then keep
  // the spacer sized as the reply streams in (no auto-follow). Ported from
  // chatModal — see useScrollToBottom.
  useLayoutEffect(() => {
    syncOnContentChange();
  }, [messages, sending, syncOnContentChange]);

  // Jump to the latest when (re)entering the assistant tab.
  useLayoutEffect(() => {
    if (tab === 0) messagesEndRef.current?.scrollIntoView();
  }, [tab, messagesEndRef]);

  // Same two rules for the Not Controlled Chat's own transcript.
  useLayoutEffect(() => {
    freeSyncOnContentChange();
  }, [freeMessages, freeSending, freeSyncOnContentChange]);

  useLayoutEffect(() => {
    if (tab === 2) freeMessagesEndRef.current?.scrollIntoView();
  }, [tab, freeMessagesEndRef]);

  // ── Generic SSE helper ──
  const openSSE = useCallback((path, { onMessage, onEvent = {}, onDone, onErr } = {}) => {
    // EventSource can't see HTTP 401s the way fetch can, so catch an expired
    // session up front and route to login instead of failing silently.
    if (isTokenExpired()) { notifyUnauthorized(); onErr && onErr({ message: 'unauthorized' }); return null; }
    const es = registerSource(new EventSource(appendTokenToUrl(path)));
    let finished = false;
    es.onmessage = (e) => { try { onMessage && onMessage(JSON.parse(e.data)); } catch (err) {} };
    Object.entries(onEvent).forEach(([name, handler]) => {
      es.addEventListener(name, (e) => { try { handler(e.data ? JSON.parse(e.data) : {}); } catch (err) {} });
    });
    es.addEventListener('end', (e) => {
      finished = true; es.close();
      let data = {}; try { data = e.data ? JSON.parse(e.data) : {}; } catch (err) {}
      onDone && onDone(data);
    });
    es.onerror = (e) => {
      if (finished) return;
      finished = true; es.close();
      if (e && e.data) { let d = {}; try { d = JSON.parse(e.data); } catch (err) {} onErr && onErr(d); }
      else { onErr && onErr({ message: 'connection_error' }); }
    };
    return es;
  }, []);

  // Map the backend's stored progress.steps into the checklist, localising the
  // generic step ids; topic steps keep their (data) label.
  // `reason` rides along for the screener step: the backend ends it with
  // status 'not_found' both when theScreener does not cover the title
  // (reason 'not_found') and when theScreener could not be asked (reason
  // 'unavailable': not configured, outage, catalogue still loading).
  const progressToSteps = useCallback((progress) => (
    (progress?.steps || []).map((s) => ({
      id: s.id,
      status: s.status,
      reason: s.reason,
      label: s.id === 'screener' ? t('are_step_screener')
        : s.id === 'analyzing' ? t('are_prog_analyzing')
        : s.id === 'argument' ? t('are_prog_argument')
        : s.id === 'bericht' ? t('are_prog_bericht')
        : s.label,
    }))
  ), [t]);

  // ── Runs API ──
  const fetchRuns = useCallback(async () => {
    setRunsLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/are/reports`, { headers: getAuthHeaders() });
      if (res.ok) setRuns(await res.json());
    } catch (e) { /* ignore */ } finally { setRunsLoading(false); }
  }, []);

  const openRun = useCallback(async (id, { instant = false, sourceLang = null } = {}) => {
    setRunError('');
    // `instant` switches to the detail screen immediately with a loader, then
    // swaps in the full report once it arrives (no ~2s wait on the list).
    // `sourceLang` (the run's original language, known from the list) lets the
    // loader tell the user we're translating when it differs from the UI language.
    if (instant) {
      setSelectedRun({ id, status: 'loading', analysis: {}, language: sourceLang });
      setDetailLoading(true);
    }
    try {
      const res = await fetch(`${API_BASE_URL}/api/are/reports/${id}?language=${language}`, { headers: getAuthHeaders() });
      if (res.ok) setSelectedRun(await res.json());
    } catch (e) { setRunError(t('are_error')); } finally { setDetailLoading(false); }
  }, [t, language]);

  // Poll a report until its background Bericht (re)generation settles, refreshing
  // the markdown in place. Only one interval runs at a time.
  const pollBericht = useCallback((reportId) => {
    if (berichtPollRef.current) { clearInterval(berichtPollRef.current); berichtPollRef.current = null; }
    let ticks = 0;
    berichtPollRef.current = setInterval(async () => {
      ticks += 1;
      try {
        // language matters: the backend serves the Bericht translated into the
        // UI language, so polling must not overwrite a translated view with the
        // canonical-language text.
        const res = await fetch(`${API_BASE_URL}/api/are/reports/${reportId}?language=${language}`, { headers: getAuthHeaders() });
        if (res.ok) {
          const rep = await res.json();
          setSelectedRun((cur) => (cur && cur.id === reportId
            ? { ...cur, bericht: rep.bericht, bericht_status: rep.bericht_status, bericht_updated_at: rep.bericht_updated_at } : cur));
          if (rep.bericht_status !== 'generating') { clearInterval(berichtPollRef.current); berichtPollRef.current = null; }
        }
      } catch (e) { /* keep polling */ }
      if (ticks > 60 && berichtPollRef.current) { clearInterval(berichtPollRef.current); berichtPollRef.current = null; }
    }, 2000);
  }, [language]);

  // Manually (re)generate the Bericht — fallback for older reports and a "neu
  // erstellen" control.
  const regenerateBericht = useCallback(async () => {
    if (!selectedRun) return;
    const id = selectedRun.id;
    setSelectedRun((cur) => (cur && cur.id === id ? { ...cur, bericht_status: 'generating' } : cur));
    try {
      await fetch(`${API_BASE_URL}/api/are/reports/${id}/bericht`, { method: 'POST', headers: getAuthHeaders() });
    } catch (e) { /* poll reflects the persisted state */ }
    pollBericht(id);
  }, [selectedRun, pollBericht]);

  // Delete confirmation via a modal (same UX as the VIP delete in Assistant Lion),
  // replacing the old window.confirm().
  const [deleteDialog, setDeleteDialog] = useState({ open: false, run: null });
  const [deleting, setDeleting] = useState(false);

  const openDeleteDialog = useCallback((run, e) => {
    if (e) e.stopPropagation();
    setDeleteDialog({ open: true, run });
  }, []);

  const closeDeleteDialog = useCallback(() => {
    if (deleting) return; // don't allow closing mid-delete
    setDeleteDialog((cur) => ({ ...cur, open: false }));
  }, [deleting]);

  const confirmDeleteRun = useCallback(async () => {
    const run = deleteDialog.run;
    if (!run) return;
    setDeleting(true);
    try {
      await fetch(`${API_BASE_URL}/api/are/reports/${run.id}`, { method: 'DELETE', headers: getAuthHeaders() });
    } catch (err) { /* ignore */ }
    setSelectedRun((cur) => (cur && cur.id === run.id ? null : cur));
    setDeleting(false);
    setDeleteDialog({ open: false, run: null });
    fetchRuns();
  }, [deleteDialog, fetchRuns]);

  const startRun = useCallback(async (id) => {
    stopRunWatchers();
    setRunning(true);
    setRunError('');
    setRunPhase('collecting');
    setRunSteps([]);
    setRunTotal(0);

    let done = false;
    const finalize = (rep) => {
      if (done) return;
      done = true;
      stopRunWatchers();
      if (rep) setSelectedRun((cur) => (cur && cur.id === id ? rep : cur));
      setRunning(false);
      setRunPhase('');
      setRunSteps([]);
      setRunTotal(0);
      fetchRuns();
    };

    // Kick off the background run — FIRE-AND-FORGET (no await). A slow/hanging
    // POST (e.g. while the assistant SSE is still closing locally) must NOT block
    // the progress poll below from being set up; the watchdog re-kicks if needed.
    fetch(`${API_BASE_URL}/api/are/reports/${id}/run`, { method: 'POST', headers: getAuthHeaders() }).catch(() => {});

    // Poll the persisted status + progress until it settles. Short requests,
    // no long-lived connection — the source of truth is the DB.
    let stuckTicks = 0;
    pollRef.current = setInterval(async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/are/reports/${id}?language=${language}`, { headers: getAuthHeaders() });
        if (!res.ok) return;
        const rep = await res.json();
        // Enrich the (possibly minimal) run object with the chosen parameters so
        // the running view can show them — without touching status/analysis.
        setSelectedRun((cur) => (cur && cur.id === id ? {
          ...cur,
          company_name: rep.company_name, ticker: rep.ticker,
          intended_positioning: rep.intended_positioning,
          weeks_back: rep.weeks_back, collection_config: rep.collection_config,
        } : cur));
        const prog = rep.progress || {};
        setRunPhase(prog.phase === 'analyzing' || rep.status === 'analyzing' ? 'analyzing' : 'collecting');
        setRunSteps(progressToSteps(prog));
        if (prog.topics_total) setRunTotal(prog.topics_total);
        // Watchdog: if the run never leaves 'planned'/'planning', the background
        // start didn't take — re-kick /run (idempotent) every ~6s until it moves.
        if (['planning', 'planned'].includes(rep.status)) {
          stuckTicks += 1;
          if (stuckTicks % 3 === 0) {
            try { await fetch(`${API_BASE_URL}/api/are/reports/${id}/run`, { method: 'POST', headers: getAuthHeaders() }); } catch (e) { /* ignore */ }
          }
        } else {
          stuckTicks = 0;
        }
        if (['ready', 'error', 'collected'].includes(rep.status)) finalize(rep);
      } catch (e) { /* keep polling */ }
    }, 2000);
  }, [progressToSteps, fetchRuns, stopRunWatchers, language]);

  // Called when the assistant created a run.
  const handleRunStarted = useCallback((d) => {
    const id = d.report_id;
    if (!id) return;
    setSelectedRun({ id, company_name: d.company, ticker: d.ticker, intended_positioning: d.positioning, status: d.status || 'planned', analysis: {} });
    setTab(1);
    fetchRuns();
    startRun(id);
  }, [fetchRuns, startRun]);

  // ── Assistant chat ──
  // Send one turn to the assistant. `resetSession` starts a fresh session and
  // replaces the transcript (used by the welcome flow and "Duplizieren").
  const sendAssistant = useCallback((text, { resetSession = false } = {}) => {
    const msg = (text || '').trim();
    if (!msg || sending) return;
    if (resetSession) sessionIdRef.current = genSessionId();
    markUserSent(); // slide this user message up to the top; reply streams below
    setMessages((m) => (resetSession
      ? [{ sender: 'user', text: msg }, { sender: 'bot', text: '' }]
      : [...m, { sender: 'user', text: msg }, { sender: 'bot', text: '' }]));
    setInput('');
    setSending(true);
    botBufRef.current = '';
    const url = `/api/are/assistant?user_input=${encodeURIComponent(msg)}&session_id=${sessionIdRef.current}&language=${language}`;
    openSSE(url, {
      onMessage: (d) => {
        if (d.type === 'text' && d.content) {
          botBufRef.current += d.content;
          setMessages((m) => { const c = [...m]; c[c.length - 1] = { sender: 'bot', text: botBufRef.current }; return c; });
        }
      },
      onEvent: { run_started: (d) => handleRunStarted(d) },
      onDone: () => {
        setSending(false);
        if (!botBufRef.current) {
          setMessages((m) => { const c = [...m]; c[c.length - 1] = { sender: 'bot', text: t('are_started_hint') }; return c; });
        }
      },
      onErr: () => {
        setSending(false);
        setMessages((m) => { const c = [...m]; c[c.length - 1] = { sender: 'bot', text: t('are_error') }; return c; });
      },
    });
  }, [sending, language, openSSE, handleRunStarted, markUserSent, t]);

  const sendMessage = useCallback(() => sendAssistant(input), [input, sendAssistant]);

  // ── Duplicate an analysis → hand off to the assistant with the prior settings,
  // which then asks whether to change anything (window runs from today by default).
  const duplicateRun = useCallback((r, e) => {
    if (e) e.stopPropagation();
    const title = `${r.company_name || ''}${r.ticker ? ` (${r.ticker})` : ''}`.trim();
    const cfg = r.collection_config || {};
    const screener = cfg.include_screener ? t('are_screener_on') : t('are_screener_off');
    const weeks = r.weeks_back != null ? ` — ${r.weeks_back} ${t('are_weeks_short')}` : '';
    const leaning = r.intended_positioning ? `, ${t('are_requested_leaning')}: ${t('are_' + r.intended_positioning)}` : '';
    const seed = `${t('are_duplicate_intro')} ${title}${weeks}, ${screener}${leaning}.`;
    setTab(0);
    sendAssistant(seed, { resetSession: true });
  }, [sendAssistant, t]);

  const newChat = useCallback(() => {
    sessionIdRef.current = genSessionId();
    setMessages([{ sender: 'bot', text: t('are_assistant_welcome'), welcome: true }]);
    setWelcomeLang(language);
    setInput('');
  }, [t, language]);

  // ── Not Controlled Chat ──
  // One turn against /api/are/free-chat. Same SSE helper as the assistant, plus
  // two extra frames: `search_status` (the model is querying the web) and
  // `sources` (the url citations behind the answer).
  const sendFree = useCallback((text) => {
    const msg = (text || '').trim();
    if (!msg || freeSending) return;
    freeMarkUserSent(); // slide this user message up; the reply streams below
    setFreeMessages((m) => [...m, { sender: 'user', text: msg }, { sender: 'bot', text: '', sources: [] }]);
    setFreeInput('');
    setFreeSending(true);
    setFreeSearching(false);
    freeBufRef.current = '';

    // Replace the trailing (bot) message, keeping the fields we don't touch.
    const patchLast = (patch) => setFreeMessages((m) => {
      const c = [...m];
      c[c.length - 1] = { ...c[c.length - 1], ...patch };
      return c;
    });

    const url = `/api/are/free-chat?user_input=${encodeURIComponent(msg)}&session_id=${freeSessionIdRef.current}&language=${language}`;
    freeEsRef.current = openSSE(url, {
      onMessage: (d) => {
        if (d.type === 'text' && d.content) {
          freeBufRef.current += d.content;
          patchLast({ text: freeBufRef.current });
        }
      },
      onEvent: {
        search_status: (d) => setFreeSearching(!!d.active),
        sources: (d) => patchLast({ sources: Array.isArray(d.sources) ? d.sources : [] }),
      },
      onDone: () => {
        freeEsRef.current = null;
        setFreeSending(false);
        setFreeSearching(false);
        if (!freeBufRef.current) patchLast({ text: t('are_free_empty') });
      },
      onErr: () => {
        freeEsRef.current = null;
        setFreeSending(false);
        setFreeSearching(false);
        // Keep whatever already streamed; only an empty bubble becomes the error.
        patchLast({ text: freeBufRef.current || t('are_error') });
      },
    });
  }, [freeSending, language, openSSE, freeMarkUserSent, t]);

  const sendFreeMessage = useCallback(() => sendFree(freeInput), [freeInput, sendFree]);

  // Stop generating. Closing the EventSource ends the turn; the backend's
  // generator still persists the partial answer to chat_history.
  const stopFree = useCallback(() => {
    if (freeEsRef.current) {
      try { freeEsRef.current.close(); } catch (e) { /* already closed */ }
      freeEsRef.current = null;
    }
    setFreeSending(false);
    setFreeSearching(false);
    setFreeMessages((m) => {
      const c = [...m];
      const last = c[c.length - 1];
      if (last && last.sender === 'bot' && !last.text) c[c.length - 1] = { ...last, text: t('are_free_stopped') };
      return c;
    });
  }, [t]);

  // A finished speech-to-speech turn joins the written conversation, so closing
  // the overlay leaves one continuous thread. The backend wrote the same pair to
  // chat_history under this session id, so the next typed turn has it in context
  // too — nothing is re-sent from here.
  const appendFreeVoiceTurn = useCallback(({ question, answer, sources }) => {
    if (!question && !answer) return;
    setFreeMessages((m) => [
      ...m,
      ...(question ? [{ sender: 'user', text: question }] : []),
      ...(answer ? [{ sender: 'bot', text: answer, sources: sources || [] }] : []),
    ]);
  }, []);

  /**
   * Download a conversation as a Word file — same endpoint, payload shape and
   * button as Assistant Bro (`{sender, text}` per message, 'user' rows get a
   * border). Used by both chat tabs (assistant and Not Controlled Chat); the
   * callers leave the greeting out — it is a UI line, not part of the exchange.
   */
  const exportChatWord = useCallback(async (payload) => {
    if (!payload.length) return;
    try {
      const res = await fetch(`${API_BASE_URL}/api/generate_word_document`, {
        method: 'POST',
        headers: getAuthHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ messages: payload }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const url = window.URL.createObjectURL(await res.blob());
      const a = document.createElement('a');
      a.href = url;
      a.download = 'ChatHistory.docx';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.error('Word export failed:', e);
    }
  }, []);

  const exportAssistantWord = useCallback(() => exportChatWord(
    messages
      .filter((m) => !m.welcome && m.text)
      .map((m) => ({ sender: m.sender === 'user' ? 'user' : 'bot', text: m.text })),
  ), [messages, exportChatWord]);

  // One addition the Not Controlled Chat needs: the source chips sit OUTSIDE the
  // bubble, so they would be lost on paper. They are appended to the answer as a
  // plain list with the address spelled out — the Word renderer keeps the text
  // of a link but drops its href inside a list item.
  const exportFreeWord = useCallback(() => {
    const withSources = (m) => (
      m.sources && m.sources.length
        ? `${m.text}\n\n**${t('are_sources')}**\n\n${m.sources
            .map((src) => `- ${src.title || src.domain || src.url} — ${src.url}`)
            .join('\n')}`
        : m.text
    );
    return exportChatWord(freeMessages
      .filter((m) => !m.welcome && m.text)
      .map((m) => ({
        sender: m.sender === 'user' ? 'user' : 'bot',
        text: m.sender === 'user' ? m.text : withSources(m),
      })));
  }, [freeMessages, t, exportChatWord]);

  const newFreeChat = useCallback(() => {
    stopFree();
    freeSessionIdRef.current = genSessionId('ncc');
    setFreeMessages([{ sender: 'bot', text: t('are_free_welcome'), welcome: true }]);
    setFreeWelcomeLang(language);
    setFreeInput('');
  }, [stopFree, t, language]);

  // ── Deepen a Posten (background job + poll), optionally with an advisor focus ──
  const deepenItem = useCallback(async (itemKey, focus = '') => {
    if (!selectedRun || deepenProgress[itemKey] != null) return;
    const reportId = selectedRun.id;
    setFocusKey(null); setFocusValue('');
    setDeepenProgress((p) => ({ ...p, [itemKey]: t('are_prog_working') }));

    const trimmedFocus = (focus || '').trim();
    let researchId = null;
    try {
      const res = await fetch(`${API_BASE_URL}/api/are/reports/${reportId}/items/${encodeURIComponent(itemKey)}/deepen`, {
        method: 'POST',
        headers: getAuthHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify(trimmedFocus ? { focus: trimmedFocus } : {}),
      });
      if (res.ok) { const d = await res.json(); researchId = d.item_research_id; }
    } catch (e) { /* poll will still observe the deepening */ }

    const clearProg = () => setDeepenProgress((p) => { const c = { ...p }; delete c[itemKey]; return c; });

    const interval = setInterval(async () => {
      try {
        const r = await fetch(`${API_BASE_URL}/api/are/reports/${reportId}?language=${language}`, { headers: getAuthHeaders() });
        if (!r.ok) return;
        const rep = await r.json();
        const ir = (rep.item_research || []).find((x) => (researchId ? x.id === researchId : x.item_key === itemKey));
        if (ir && ['ready', 'error'].includes(ir.status)) {
          clearInterval(interval);
          deepenPollsRef.current = deepenPollsRef.current.filter((iv) => iv !== interval);
          setSelectedRun((cur) => (cur && cur.id === reportId ? rep : cur));
          clearProg();
          // A successful deepening folds new findings into the Bericht — watch it refresh.
          if (ir.status === 'ready') pollBericht(reportId);
        }
      } catch (e) { /* keep polling */ }
    }, 2000);
    deepenPollsRef.current.push(interval);
  }, [selectedRun, deepenProgress, pollBericht, t, language]);

  // ── Argumentation ──
  // (Re)generate the argument for one positioning. Each call appends a fresh
  // argumentation; the position selector shows the latest one per positioning
  // (cached on switch — see renderPosition). The Refresh button calls this again.
  const requestArgument = useCallback(async (positioning) => {
    if (!selectedRun || argLoading) return;
    setArgLoading(true); setArgLoadingPos(positioning); setArgError(''); setSelectedPos(positioning);
    try {
      const res = await fetch(`${API_BASE_URL}/api/are/reports/${selectedRun.id}/argument`, {
        method: 'POST',
        headers: getAuthHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ positioning }),
      });
      if (res.ok) { await openRun(selectedRun.id); }
      else { const e = await res.json().catch(() => ({})); setArgError(e.error || t('are_error')); }
    } catch (e) { setArgError(t('are_error')); } finally { setArgLoading(false); setArgLoadingPos(null); }
  }, [selectedRun, argLoading, openRun, t]);

  // Switch tabs: show the cached argument if present, otherwise generate it once.
  const selectPos = useCallback((p) => {
    setSelectedPos(p);
    const exists = (selectedRun?.argumentations || []).some((arg) => arg.positioning === p);
    if (!exists && !argLoading) requestArgument(p);
  }, [selectedRun, argLoading, requestArgument]);

  // Reset per-report view state when a *different* report is opened (not on
  // same-report refetches, so the chosen tab/view survives a generate/refresh).
  useEffect(() => {
    setSelectedPos(null); setArgError(''); setDetailsOpen(false); setDetailsTab(0);
    if (berichtPollRef.current) { clearInterval(berichtPollRef.current); berichtPollRef.current = null; }
  }, [selectedRun?.id]);

  // Re-fetch the open report when the UI language changes, so analysis, Bericht,
  // argumentations and deepenings arrive translated (server-side, cache-first).
  const langRef = useRef(language);
  useEffect(() => {
    if (langRef.current === language) return;
    langRef.current = language;
    if (selectedRun?.id && !IN_PROGRESS.includes(selectedRun.status)) {
      openRun(selectedRun.id, { instant: true, sourceLang: selectedRun.language || null });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [language]);

  // If a report is opened that is 'ready' but has no Bericht yet (created before
  // the feature, or a prior generation failed), kick off generation once.
  useEffect(() => {
    if (selectedRun?.status === 'ready' && selectedRun?.analysis && Object.keys(selectedRun.analysis).length > 0
        && !selectedRun.bericht && selectedRun.bericht_status !== 'generating') {
      regenerateBericht();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRun?.id, selectedRun?.status]);

  useEffect(() => { if (tab === 1 && !selectedRun) fetchRuns(); }, [tab, selectedRun, fetchRuns]);

  // ── Narrative CRUD (operates on the analysis JSONB on the server) ──
  const applyAnalysis = useCallback((analysis) => {
    setSelectedRun((cur) => (cur ? { ...cur, analysis } : cur));
  }, []);

  // After an edit: the CRUD response returns the analysis in the report's
  // canonical language. If we're viewing in a different language, re-fetch so the
  // (now cache-invalidated) analysis comes back re-translated; otherwise apply
  // the returned analysis directly (snappy, no extra round-trip).
  const applyOrRefetch = useCallback((analysis) => {
    if (selectedRun && selectedRun.language && language && language !== selectedRun.language) {
      openRun(selectedRun.id);
    } else {
      applyAnalysis(analysis);
    }
  }, [selectedRun, language, openRun, applyAnalysis]);

  const addNarrative = useCallback(async (field, label) => {
    const text = (label || '').trim();
    if (!selectedRun || !text) return;
    setCrudKey(`add:${field}`);
    try {
      const res = await fetch(`${API_BASE_URL}/api/are/reports/${selectedRun.id}/narratives`, {
        method: 'POST',
        headers: getAuthHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ category: field, label: text }),
      });
      if (res.ok) { const d = await res.json(); applyOrRefetch(d.analysis); setAddingCat(null); setAddValue(''); pollBericht(selectedRun.id); }
    } catch (e) { /* ignore */ } finally { setCrudKey(null); }
  }, [selectedRun, applyOrRefetch, pollBericht]);

  const saveNarrative = useCallback(async (itemKey, label) => {
    const text = (label || '').trim();
    if (!selectedRun || !text) return;
    setCrudKey(itemKey);
    try {
      const res = await fetch(`${API_BASE_URL}/api/are/reports/${selectedRun.id}/narratives/${encodeURIComponent(itemKey)}`, {
        method: 'PATCH',
        headers: getAuthHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ label: text }),
      });
      if (res.ok) { const d = await res.json(); applyOrRefetch(d.analysis); setEditingKey(null); setEditValue(''); pollBericht(selectedRun.id); }
    } catch (e) { /* ignore */ } finally { setCrudKey(null); }
  }, [selectedRun, applyOrRefetch, pollBericht]);

  const removeNarrative = useCallback(async (itemKey) => {
    if (!selectedRun || !window.confirm(t('are_narr_delete_confirm'))) return;
    setCrudKey(itemKey);
    try {
      const res = await fetch(`${API_BASE_URL}/api/are/reports/${selectedRun.id}/narratives/${encodeURIComponent(itemKey)}`, {
        method: 'DELETE', headers: getAuthHeaders(),
      });
      if (res.ok) {
        const d = await res.json();
        applyOrRefetch(d.analysis);
        pollBericht(selectedRun.id);
      }
    } catch (e) { /* ignore */ } finally { setCrudKey(null); }
  }, [selectedRun, applyOrRefetch, t, pollBericht]);

  // ── Helpers for rendering ──
  const evidenceById = useMemo(() => {
    const map = {};
    (selectedRun?.evidence || []).forEach((e) => { map[e.id] = e; });
    (selectedRun?.item_research || []).forEach((ir) => (ir.evidence || []).forEach((e) => { map[e.id] = e; }));
    return map;
  }, [selectedRun]);

  const deepeningsByKey = useMemo(() => {
    const map = {};
    (selectedRun?.item_research || []).forEach((ir) => {
      if (!map[ir.item_key]) map[ir.item_key] = [];
      map[ir.item_key].push(ir);
    });
    return map;
  }, [selectedRun]);

  // item_key -> { label, color, field } so takeaway tags can name + locate a narrative.
  const narrativeByKey = useMemo(() => {
    const map = {};
    const a = selectedRun?.analysis || {};
    POSTEN_CATEGORIES.forEach((cat) => {
      const items = Array.isArray(a[cat.field]) ? a[cat.field] : [];
      items.forEach((it) => {
        if (it && it.item_key) {
          const label = cat.field === 'turning_points'
            ? `${it.date ? it.date + ' — ' : ''}${it.description || it.label || ''}`
            : itemLabel(it);
          map[it.item_key] = { label, field: cat.field, Icon: cat.Icon };
        }
      });
    });
    return map;
  }, [selectedRun]);

  // ── "Mehr Details" page openers ──
  const openDetails = useCallback((tabIndex = 0) => { setDetailsTab(tabIndex); setDetailsOpen(true); }, []);
  const closeDetails = useCallback(() => { setDetailsOpen(false); setEditingKey(null); setFocusKey(null); setAddingCat(null); }, []);
  // Open the Themen tab with a specific narrative expanded (used by the linking
  // tags in "Position begründen").
  const openNarrative = useCallback((itemKey) => {
    setDetailsTab(0);
    setDetailsOpen(true);
    setOpenMap((m) => ({ ...m, [`posten:${itemKey}`]: true }));
  }, []);

  // A source is openable if it has a URL, or if it's the Screener (then we open
  // the actual TheScreener PDF factsheet in the report's language, not the site).
  // The factsheet route needs the login token: it is fetched with the
  // Authorization header and shown from a blob (no JWT in the URL any more).
  // If the route has no report for the title it redirects to theScreener's
  // site; the evidence's own public URL is then the better target.
  const sourceHasLink = (e) => !!(e && (e.url || e.origin === 'screener'));
  const openSource = (e) => {
    if (!e) return;
    if (e.origin === 'screener' && selectedRun?.id) {
      const publicUrl = e.url && /^https?:\/\//i.test(e.url) && !isProtectedFileUrl(e.url) ? e.url : undefined;
      openAuthenticatedFile(`/api/are/reports/${selectedRun.id}/screener.pdf`, { fallbackUrl: publicUrl });
    } else if (e.url && isProtectedFileUrl(e.url)) {
      openAuthenticatedFile(e.url);
    } else if (e.url) {
      window.open(e.url, '_blank', 'noopener,noreferrer');
    }
  };

  const renderSourceChips = (ids) => {
    const list = (ids || []).map((id) => evidenceById[id]).filter(Boolean);
    if (!list.length) return null;
    return (
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
        {list.map((e) => (
          <Chip
            key={e.id}
            size="small"
            label={e.source_name || e.title || t('are_sources')}
            icon={sourceHasLink(e) ? <OpenInNewIcon sx={{ fontSize: 14 }} /> : undefined}
            onClick={sourceHasLink(e) ? () => openSource(e) : undefined}
            clickable={sourceHasLink(e)}
            sx={{ fontSize: 11, height: 22, bgcolor: 'action.hover' }}
          />
        ))}
      </Box>
    );
  };

  // ── Render: one Posten as an expandable (Themen tab) ──
  // Collapsed = category icon + label + status badges. Expanded reveals the
  // same options as before: attached sources, Vertiefen, edit and remove.
  const renderPostenItem = (item, category) => {
    const key = item.item_key;
    const isTurning = category.field === 'turning_points';
    const label = isTurning
      ? `${item.date ? item.date + ' — ' : ''}${item.description || item.label || ''}`
      : itemLabel(item);
    const deepening = deepeningsByKey[key] || [];
    const deepCount = deepening.length;
    const inProgress = deepenProgress[key] != null;
    const editing = editingKey === key;
    const focusing = focusKey === key;
    const isAdvisor = item.source === 'advisor';
    const open = isOpen(`posten:${key}`, false);

    return (
      <Box
        key={key}
        sx={{
          borderRadius: '10px', border: 1, borderColor: 'divider', bgcolor: 'action.hover',
          overflow: 'hidden', transition: 'border-color .15s', '&:hover': { borderColor: hexA(ACCENT, 0.4) },
        }}
      >
        {editing ? (
          <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'flex-start', p: 1.5 }}>
            <TextField
              autoFocus value={editValue} onChange={(e) => setEditValue(e.target.value)}
              multiline maxRows={4} size="small" fullWidth
              onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); saveNarrative(key, editValue); } }}
              sx={{ '& .MuiOutlinedInput-root': { bgcolor: 'background.paper', fontSize: 13 } }}
            />
            <IconButton size="small" disabled={crudKey === key || !editValue.trim()} onClick={() => saveNarrative(key, editValue)} sx={{ color: 'primary.main' }}>
              {crudKey === key ? <CircularProgress size={16} /> : <CheckIcon fontSize="small" />}
            </IconButton>
            <IconButton size="small" onClick={() => { setEditingKey(null); setEditValue(''); }} sx={{ color: 'text.secondary' }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        ) : (
          <>
            <Box onClick={() => toggleSection(`posten:${key}`, false)}
              sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75, p: 1.5, cursor: 'pointer', userSelect: 'none' }}>
              <Typography sx={{ fontSize: 13, lineHeight: 1.55, color: 'text.primary', flex: 1,
                ...(open ? {} : { overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }) }}>
                {label}
              </Typography>
              {isAdvisor && <Chip size="small" label={t('are_narr_advisor')} sx={{ height: 18, fontSize: 10, bgcolor: hexA(ACCENT, 0.12), color: 'primary.main', flexShrink: 0 }} />}
              {deepCount > 0 && <Chip size="small" icon={<AutoAwesomeIcon sx={{ fontSize: 12 }} />} label={deepCount} sx={{ height: 18, fontSize: 10, flexShrink: 0 }} />}
              {inProgress && <CircularProgress size={13} sx={{ color: 'primary.main', flexShrink: 0, mt: '2px' }} />}
              <ExpandMoreIcon sx={{ fontSize: 20, color: 'text.secondary', flexShrink: 0, transition: 'transform .2s', transform: open ? 'rotate(180deg)' : 'none' }} />
            </Box>

            <Collapse in={open} timeout="auto" unmountOnExit>
              <Box sx={{ px: 1.5, pb: 1.5 }}>
                {renderSourceChips(item.evidence_ids)}

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1, flexWrap: 'wrap' }}>
                  {inProgress ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CircularProgress size={14} sx={{ color: 'primary.main' }} />
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>{deepenProgress[key]}</Typography>
                    </Box>
                  ) : focusing ? (
                    <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'flex-start', width: '100%' }}>
                      <TextField
                        autoFocus value={focusValue} onChange={(e) => setFocusValue(e.target.value)}
                        multiline maxRows={3} size="small" fullWidth placeholder={t('are_deepen_focus_placeholder')}
                        onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); deepenItem(key, focusValue); } }}
                        sx={{ '& .MuiOutlinedInput-root': { bgcolor: 'background.paper', fontSize: 12.5 } }}
                      />
                      <IconButton size="small" title={t('are_deepen')} onClick={() => deepenItem(key, focusValue)} sx={{ color: 'primary.main' }}>
                        <AutoAwesomeIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                      <IconButton size="small" onClick={() => { setFocusKey(null); setFocusValue(''); }} sx={{ color: 'text.secondary' }}>
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  ) : (
                    <>
                      <Button
                        size="small" variant="text" startIcon={<AutoAwesomeIcon sx={{ fontSize: 16 }} />}
                        onClick={() => { setFocusKey(key); setFocusValue(''); }}
                        sx={{ textTransform: 'none', fontSize: 12, color: 'primary.main', p: 0.25 }}
                      >
                        {t('are_deepen')}
                      </Button>
                      <Box sx={{ flex: 1 }} />
                      <IconButton size="small" title={t('are_narr_edit')} onClick={() => { setEditingKey(key); setEditValue(label); }}
                        sx={{ color: 'text.secondary', p: 0.25, '&:hover': { color: 'primary.main' } }}>
                        <EditOutlinedIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                      <IconButton size="small" title={t('are_narr_delete')} disabled={crudKey === key} onClick={() => removeNarrative(key)}
                        sx={{ color: 'text.secondary', p: 0.25, '&:hover': { color: 'error.main' } }}>
                        <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </>
                  )}
                </Box>

                {deepening.map((ir) => {
          const dOpen = isOpen(`deep:${ir.id}`, true);
          return (
            <Box key={ir.id} sx={{ mt: 1, borderRadius: '10px', bgcolor: 'rgba(25,208,239,0.06)', overflow: 'hidden' }}>
              <Box onClick={() => toggleSection(`deep:${ir.id}`, true)}
                sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', userSelect: 'none', px: 1.25, pt: 1, pb: 0.5 }}>
                <Typography variant="caption" sx={{ fontWeight: 700, color: 'primary.main', textTransform: 'uppercase', letterSpacing: '0.04em', flex: 1 }}>
                  {t('are_deepen_result')}
                </Typography>
                <ExpandMoreIcon sx={{ fontSize: 18, color: 'text.secondary', transition: 'transform .2s', transform: dOpen ? 'rotate(180deg)' : 'none' }} />
              </Box>
              <Collapse in={dOpen} timeout="auto">
                <Box sx={{ px: 1.25, pb: 1.25 }}>
                  {ir.result?.focus && (
                    <Typography variant="caption" sx={{ display: 'block', mb: 0.5, color: 'primary.main', fontStyle: 'italic' }}>
                      {t('are_deepen_focus_label')}: {ir.result.focus}
                    </Typography>
                  )}
                  {ir.status === 'ready' ? (
                    <>
                      {ir.result?.summary && (
                        <Typography sx={{ fontSize: 12.5, lineHeight: 1.6, color: 'text.primary' }}>{ir.result.summary}</Typography>
                      )}
                      {Array.isArray(ir.result?.new_developments) && ir.result.new_developments.length > 0 && (
                        <Box sx={{ mt: 0.75 }}>
                          <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>{t('are_new_developments')}</Typography>
                          {ir.result.new_developments.map((nd, i) => (
                            <Box key={i} sx={{ mt: 0.25 }}>
                              <Typography sx={{ fontSize: 12, color: 'text.primary' }}>• {itemLabel(nd)}</Typography>
                              {renderSourceChips(nd.evidence_ids)}
                            </Box>
                          ))}
                        </Box>
                      )}
                      {Array.isArray(ir.result?.open_questions) && ir.result.open_questions.length > 0 && (
                        <Box sx={{ mt: 0.75 }}>
                          <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>{t('are_open_questions')}</Typography>
                          {ir.result.open_questions.map((q, i) => (
                            <Typography key={i} sx={{ fontSize: 12, color: 'text.secondary' }}>• {q}</Typography>
                          ))}
                        </Box>
                      )}
                      {renderSourceChips(ir.result?.evidence_ids)}
                    </>
                  ) : (
                    <Typography variant="caption" sx={{ color: ir.status === 'error' ? 'error.main' : 'text.secondary' }}>
                      {ir.status === 'error' ? t('are_error') : t('are_prog_analyzing')}
                    </Typography>
                  )}
                </Box>
              </Collapse>
            </Box>
          );
        })}
              </Box>
            </Collapse>
          </>
        )}
      </Box>
    );
  };

  // A clickable, clearly-interactive tag (arrow + blue pill) that opens the
  // linked narrative in the "Mehr Details" page (Themen tab).
  const renderNarrativeTag = (nid) => {
    const info = narrativeByKey[nid];
    if (!info) return null;
    return (
      <Chip
        key={nid} size="small" clickable onClick={() => openNarrative(nid)}
        icon={<ArrowForwardIcon sx={{ fontSize: 13 }} />}
        label={truncate(info.label, 32)}
        sx={{
          fontSize: 11, height: 22, fontWeight: 500, borderRadius: '10px',
          color: 'text.secondary', bgcolor: 'action.hover', border: 'none',
          cursor: 'pointer', transition: 'color .15s, background-color .15s',
          '& .MuiChip-icon': { fontSize: 13, color: 'text.disabled', ml: 0.4, mr: -0.3 },
          '& .MuiChip-label': { px: 0.6 },
          '&:hover': { bgcolor: hexA(ACCENT, 0.10), color: 'primary.main', '& .MuiChip-icon': { color: 'primary.main' } },
        }}
      />
    );
  };

  // A supporting/contradicting point: one crisp sentence + tags that link down
  // to the underlying narrative(s) in Details.
  const renderArgPoint = (s, i) => (
    <Box key={i} sx={{ display: 'flex', gap: 0.75, mb: 1 }}>
      <Typography sx={{ fontSize: 12.5, lineHeight: 1.5, color: 'text.secondary', flexShrink: 0 }}>•</Typography>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontSize: 12.5, lineHeight: 1.5, color: 'text.primary' }}>{itemLabel(s) || s.point}</Typography>
        {Array.isArray(s.narrative_ids) && s.narrative_ids.some((nid) => narrativeByKey[nid]) && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.6, mt: 0.6 }}>
            {s.narrative_ids.map(renderNarrativeTag)}
          </Box>
        )}
      </Box>
    </Box>
  );

  // A calm group of supporting/contradicting points under one soft heading.
  const renderArgGroup = (title, Icon, color, points) => {
    if (!Array.isArray(points) || points.length === 0) return null;
    return (
      <Box sx={{ mb: 1.75 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.75 }}>
          <Box sx={{ width: '4px', alignSelf: 'stretch', minHeight: 18, borderRadius: '10px', bgcolor: 'primary.main', flexShrink: 0 }} />
          <Icon sx={{ fontSize: 17, color }} />
          <Typography sx={{ fontSize: 13, fontWeight: 600, color: 'text.primary' }}>{title}</Typography>
        </Box>
        {/* Content aligns with the icon's left edge (4px line + 6px gap). */}
        <Box sx={{ pl: 1.25 }}>{points.map(renderArgPoint)}</Box>
      </Box>
    );
  };

  // ── Render: the verdict — calm supporting / contradicting / risk groups ──
  const renderVerdict = (arg) => {
    const r = arg.result || {};
    return (
      <Box key={arg.id}>
        {renderArgGroup(t('are_supporting'), CheckCircleOutlineIcon, 'success.main', r.supporting_narratives)}
        {renderArgGroup(t('are_contradicting'), HighlightOffIcon, 'error.main', r.contradicting_narratives)}
        {Array.isArray(r.risks_against_thesis) && r.risks_against_thesis.length > 0 && (
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.75 }}>
              <Box sx={{ width: '4px', alignSelf: 'stretch', minHeight: 18, borderRadius: '10px', bgcolor: 'primary.main', flexShrink: 0 }} />
              <WarningAmberIcon sx={{ fontSize: 17, color: 'warning.main' }} />
              <Typography sx={{ fontSize: 13, fontWeight: 600, color: 'text.primary' }}>{t('are_risks_against')}</Typography>
            </Box>
            <Box sx={{ pl: 1.25 }}>
              {r.risks_against_thesis.map((s, i) => (<Typography key={i} sx={{ fontSize: 12.5, color: 'text.secondary', mb: 0.4 }}>• {s}</Typography>))}
            </Box>
          </Box>
        )}
      </Box>
    );
  };

  // ── Render: Position begründen — calm segmented selector + verdict ──
  // No auto-fire and no busy tab-strip: a report's intended position (already
  // argued during the run) shows straight away; other positions generate only
  // on an explicit click.
  const renderPosition = (run) => {
    const list = run.argumentations || [];
    const argByPos = {};
    list.forEach((arg) => { if (!argByPos[arg.positioning]) argByPos[arg.positioning] = arg; });
    const effectivePos = selectedPos || (list[0] && list[0].positioning) || run.intended_positioning || null;
    const current = effectivePos ? argByPos[effectivePos] : null;
    const loadingThis = argLoading && argLoadingPos === effectivePos;
    return (
      <>
        <Typography sx={{ fontSize: 12.5, color: 'text.secondary', mb: 1 }}>{t('are_position_choose')}</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <Box sx={{ display: 'inline-flex', p: 0.4, borderRadius: '10px', bgcolor: 'action.hover', border: 1, borderColor: 'divider' }}>
            {['buy', 'hold', 'sell'].map((p) => {
              const isActive = effectivePos === p;
              return (
                <Box key={p} onClick={() => selectPos(p)}
                  sx={{ cursor: 'pointer', userSelect: 'none', px: 2, py: 0.7, borderRadius: '10px', fontSize: 13,
                    fontWeight: isActive ? 700 : 500, transition: 'all .15s',
                    color: isActive ? 'primary.contrastText' : 'text.secondary',
                    bgcolor: isActive ? 'primary.main' : 'transparent',
                    '&:hover': { color: isActive ? 'primary.contrastText' : 'primary.main' } }}>
                  {t('are_' + p)}
                </Box>
              );
            })}
          </Box>
          <Box sx={{ flex: 1 }} />
          {current && (
            <Button size="small" variant="text" startIcon={<RefreshIcon sx={{ fontSize: 16 }} />}
              disabled={argLoading} onClick={() => requestArgument(effectivePos)}
              sx={{ textTransform: 'none', fontSize: 12, color: 'text.secondary', flexShrink: 0 }}>
              {t('are_refresh_arg')}
            </Button>
          )}
        </Box>

        {loadingThis ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 2 }}>
            <CircularProgress size={18} sx={{ color: 'primary.main' }} />
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>{t('are_prog_argument')}</Typography>
          </Box>
        ) : current ? (
          renderVerdict(current)
        ) : effectivePos ? (
          <Box sx={{ py: 1 }}>
            <Typography sx={{ fontSize: 13, color: 'text.secondary', mb: 1 }}>{t('are_no_arg_yet')}</Typography>
            <Button variant="outlined" disabled={argLoading} onClick={() => requestArgument(effectivePos)} sx={{ textTransform: 'none' }}>
              {t('are_generate_arg')}
            </Button>
          </Box>
        ) : (
          <Typography sx={{ fontSize: 13, color: 'text.secondary', py: 1 }}>{t('are_position_pick_hint')}</Typography>
        )}
        {argError && <Typography sx={{ color: 'error.main', fontSize: 13, mt: 1 }}>{argError}</Typography>}
      </>
    );
  };

  // ── Render: a uniform collapsible section (blue title left, rotating arrow
  // right, animated Collapse). Used identically for Position, Kernaussagen,
  // Details and Quellen.
  const renderSection = (key, title, defaultOpen, content, Icon) => {
    const open = isOpen(key, defaultOpen);
    return (
      <Box sx={{ mb: 1.5 }}>
        <Box onClick={() => toggleSection(key, defaultOpen)}
          sx={{ display: 'flex', alignItems: 'center', gap: 0.75, cursor: 'pointer', userSelect: 'none', py: 0.85,
            borderBottom: 1, borderColor: 'rgba(25,208,239,0.20)' }}>
          {Icon && <Icon sx={{ fontSize: 18, color: 'primary.main' }} />}
          <Typography sx={{ fontSize: 13, fontWeight: 700, lineHeight: 1, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'primary.main', flex: 1 }}>
            {title}
          </Typography>
          <ExpandMoreIcon sx={{ fontSize: 22, color: 'primary.main', transition: 'transform .25s', transform: open ? 'rotate(180deg)' : 'none' }} />
        </Box>
        <Collapse in={open} timeout="auto" unmountOnExit>
          <Box sx={{ pt: 1.5 }}>{content}</Box>
        </Collapse>
      </Box>
    );
  };

  // ── Render: live "market research" visualization while a run is in progress ──
  const renderRunningView = () => {
    const topicSteps = runSteps.filter((s) => String(s.id).startsWith('topic-'));
    const doneTopics = topicSteps.filter((s) => s.status === 'done').length;
    const total = runTotal || topicSteps.length;
    const run = selectedRun || {};
    const cfg = run.collection_config;
    const titleLabel = `${run.company_name || ''}${run.ticker ? ` (${run.ticker})` : ''}`.trim();
    // Collection steps (Screener + topics) fill the grid; the finishing steps
    // (analyse / argument / Briefing) sit below a divider.
    const collectionSteps = runSteps.filter((s) => s.id === 'screener' || String(s.id).startsWith('topic-'));
    const finalSteps = runSteps.filter((s) => ['analyzing', 'argument', 'bericht'].includes(s.id));
    // A step is settled when it is 'done' or — for the Screener step —
    // 'not_found' (no theScreener research; the run carries on without it).
    // Settled steps drop the spinner and the bold weight. The text says why:
    // "not found" only when theScreener does not cover the title; during an
    // outage (reason 'unavailable') it says "currently unavailable" instead.
    const stepRow = (s) => {
      const notFound = s.status === 'not_found';
      const settled = s.status === 'done' || notFound;
      const notFoundText = s.reason === 'unavailable' ? t('are_step_unavailable') : t('are_step_not_found');
      return (
        <Box key={s.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
          <Box sx={{ width: 22, height: 22, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            {s.status === 'done' ? (
              <svg className="are-check-svg" width="20" height="20" viewBox="0 0 24 24">
                <circle cx="12" cy="12" r="11" fill="rgba(61,220,151,0.12)" />
                <path d="M6 12.5l4 4 8-9" fill="none" stroke="#3ddc97" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            ) : notFound ? (
              <Box component="svg" width={20} height={20} viewBox="0 0 24 24" aria-hidden="true" sx={{ color: 'text.secondary' }}>
                <circle cx="12" cy="12" r="11" fill="currentColor" fillOpacity="0.12" />
                <path d="M7.5 12h9" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
              </Box>
            ) : (
              <CircularProgress size={15} thickness={5} sx={{ color: 'primary.main' }} />
            )}
          </Box>
          <Typography sx={{ fontSize: 13, lineHeight: 1.3, color: settled ? 'text.secondary' : 'text.primary',
            fontWeight: settled ? 400 : 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>
            {notFound ? `${s.label} — ${notFoundText}` : s.label}
          </Typography>
        </Box>
      );
    };
    return (
      <Box sx={{ py: 4, display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
        <div className="are-research are-research-lg" aria-hidden="true">
          <div className="are-glow" />
          <div className="are-ring" />
          <div className="are-ring are-ring2" />
          <div className="are-orbit"><span className="are-dot" /></div>
          <div className="are-orbit are-o2"><span className="are-dot" /></div>
          <div className="are-lens"><TravelExploreIcon sx={{ fontSize: 88, color: 'primary.main' }} /></div>
        </div>
        <Typography sx={{ fontWeight: 600, fontSize: 19, mt: 2 }}>
          {runPhase === 'analyzing' ? t('are_hero_analyzing') : t('are_hero_researching')}
        </Typography>
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>{t('are_hero_subtitle')}</Typography>

        {/* Compact summary of the advisor's selection (title, leaning, window, Screener) */}
        {(titleLabel || run.intended_positioning || cfg || run.weeks_back != null) && (
          <Box sx={{ display: 'flex', gap: 0.6, flexWrap: 'wrap', justifyContent: 'center', mt: 1.75, maxWidth: 380 }}>
            {titleLabel && (
              <Chip size="small" label={titleLabel} sx={{ height: 24, fontSize: 11.5, fontWeight: 700, bgcolor: hexA(ACCENT, 0.14), color: 'primary.main' }} />
            )}
            {run.intended_positioning && (
              <Chip size="small" label={t('are_' + run.intended_positioning)} sx={{ height: 24, fontSize: 11.5, fontWeight: 600, bgcolor: 'action.hover', color: 'text.primary' }} />
            )}
            {run.weeks_back != null && (
              <Chip size="small" label={`${run.weeks_back} ${t('are_weeks_short')}`} sx={{ height: 24, fontSize: 11.5, bgcolor: 'action.hover', color: 'text.secondary' }} />
            )}
            {cfg && (
              <Chip size="small" label={cfg.include_screener ? t('are_screener_on') : t('are_screener_off')} sx={{ height: 24, fontSize: 11.5, bgcolor: 'action.hover', color: 'text.secondary' }} />
            )}
          </Box>
        )}

        {total > 0 && (
          <Typography sx={{ mt: 1.25, fontSize: 14, fontWeight: 700, color: 'primary.main' }}>
            {doneTopics} / {total}
          </Typography>
        )}

        {/* Collection steps side-by-side (2 cols) — compact, checkmarks aligned per column */}
        {collectionSteps.length > 0 && (
          <Box sx={{ mt: 1.5, width: '100%', maxWidth: 560, mx: 'auto', display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, columnGap: 3, rowGap: 0.85, textAlign: 'left' }}>
            {collectionSteps.map((s) => stepRow(s))}
          </Box>
        )}

        {/* Once collection is done: a thin divider, then the finishing steps */}
        {finalSteps.length > 0 && (
          <>
            <Box className="are-divider" sx={{ mt: 2, mb: 1.5, width: '100%', maxWidth: 560, mx: 'auto' }} />
            <Box sx={{ display: 'inline-flex', flexDirection: 'column', gap: 0.85, alignItems: 'flex-start', textAlign: 'left', mx: 'auto' }}>
              {finalSteps.map((s) => stepRow(s))}
            </Box>
          </>
        )}
      </Box>
    );
  };

  // ══ Bericht + "Mehr Details" ════════════════════════════════════════════

  // Markdown styling for the Bericht body (headings, lists, scoreboard table).
  const berichtSx = {
    color: 'text.primary', fontSize: 13.5, lineHeight: 1.7,
    '& h1': { fontSize: 18, fontWeight: 700, mt: 0, mb: 1 },
    '& h2': { fontSize: 15, fontWeight: 700, mt: 2, mb: 0.75, color: 'primary.main' },
    '& h3': { fontSize: 14, fontWeight: 700, mt: 1.5, mb: 0.5 },
    '& p': { my: 0.75 },
    '& ul, & ol': { pl: 2.5, my: 0.5 },
    '& li': { mb: 0.25 },
    '& strong': { fontWeight: 700 },
    '& a': { color: 'primary.main' },
    '& table': { borderCollapse: 'collapse', my: 1, width: '100%', fontSize: 12.5, display: 'block', overflowX: 'auto' },
    '& th, & td': { border: '1px solid', borderColor: 'divider', p: 0.75, textAlign: 'left' },
    '& th': { bgcolor: 'action.hover', fontWeight: 700 },
    '& hr': { display: 'none' },
  };

  // The readable Bericht — the advisor's main reading surface.
  const renderBericht = (run) => {
    const text = stripTitleDate(run.bericht);
    const generating = run.bericht_status === 'generating';
    if (!text) {
      if (generating) {
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
            <CircularProgress size={16} sx={{ color: 'primary.main' }} />
            <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>{t('are_bericht_generating')}</Typography>
          </Box>
        );
      }
      // No text and not generating (idle / a failed generation) → offer a retry.
      return (
        <Box sx={{ py: 1 }}>
          <Typography sx={{ fontSize: 13, color: 'text.secondary', mb: 1 }}>
            {run.bericht_status === 'error' ? t('are_error') : t('are_bericht_generating')}
          </Typography>
          <Button variant="outlined" onClick={regenerateBericht} sx={{ textTransform: 'none' }}>
            {t('are_bericht_regenerate')}
          </Button>
        </Box>
      );
    }
    return (
      <Box>
        {/* Float right so the report's own title (the markdown H1) sits to its left. */}
        <Box sx={{ float: 'right', ml: 1, mb: 0.5 }}>
          <Button size="small" variant="text" startIcon={<RefreshIcon sx={{ fontSize: 15 }} />}
            disabled={generating} onClick={regenerateBericht}
            sx={{ textTransform: 'none', fontSize: 11.5, color: 'text.secondary', whiteSpace: 'nowrap', py: 0 }}>
            {t('are_bericht_regenerate')}
          </Button>
        </Box>
        {(run.bericht_updated_at || run.created_at) && (
          <Typography sx={{ fontSize: 11, color: 'text.secondary', mb: 0.75 }}>
            {t('are_last_updated')}: {formatDateTime(run.bericht_updated_at || run.created_at, language)}
          </Typography>
        )}
        {generating && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.25, px: 1, py: 0.75, borderRadius: '10px', bgcolor: hexA(ACCENT, 0.08) }}>
            <CircularProgress size={14} sx={{ color: 'primary.main' }} />
            <Typography sx={{ fontSize: 12, color: 'primary.main' }}>{t('are_bericht_updating')}</Typography>
          </Box>
        )}
        <Box sx={berichtSx}>
          <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw]} components={chatMarkdownComponents}>{alignNumericColumns(text)}</ReactMarkdown>
        </Box>
      </Box>
    );
  };

  // Plain hyperlink in the main report that opens the "Mehr Details" page.
  const renderDetailsEntry = (a) => {
    const total = POSTEN_CATEGORIES.reduce((n, c) => n + (Array.isArray(a[c.field]) ? a[c.field].length : 0), 0);
    return (
      <Box sx={{ mt: 2.5, mb: 1.5 }}>
        <Box component="span" onClick={() => openDetails(0)}
          sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25, cursor: 'pointer',
            color: 'primary.main', fontSize: 13.5, fontWeight: 600,
            '& .more-arrow': { transition: 'transform .2s ease' },
            '&:hover .more-text': { textDecoration: 'underline' },
            '&:hover .more-arrow': { transform: 'translateX(4px)' } }}>
          <Box component="span" className="more-text">{t('are_more_details')} ({total})</Box>
          <ArrowForwardIcon className="more-arrow" sx={{ fontSize: 16 }} />
        </Box>
      </Box>
    );
  };

  // One source row in the Quellen tab.
  const renderSourceRow = (e) => (
    <Box key={e.id} sx={{ py: 0.75, borderBottom: 1, borderColor: 'divider' }}>
      {/* Date · source · link on one baseline, all the same size, vertically centered */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Typography component="span" sx={{ fontSize: 12.5, lineHeight: 1.4, color: 'text.primary' }}>
          {e.published_date ? `${formatDate(e.published_date, language)} · ` : ''}{e.source_name || e.origin}
        </Typography>
        {sourceHasLink(e) && (
          <OpenInNewIcon
            onClick={() => openSource(e)}
            sx={{ fontSize: 12.5, color: 'primary.main', cursor: 'pointer', flexShrink: 0, '&:hover': { opacity: 0.7 } }}
          />
        )}
      </Box>
      <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>{e.title}</Typography>
    </Box>
  );

  // The inline "add narrative" form, shown below the entries while a category's
  // add is active (the trigger button lives in the subsection header).
  const renderAddNarrative = (field) => (
    <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'flex-start' }}>
      <TextField autoFocus value={addValue} onChange={(e) => setAddValue(e.target.value)}
        multiline maxRows={3} size="small" fullWidth placeholder={t('are_narr_add_placeholder')}
        onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); addNarrative(field, addValue); } }}
        sx={{ '& .MuiOutlinedInput-root': { bgcolor: 'background.paper', fontSize: 13 } }} />
      <IconButton size="small" disabled={crudKey === `add:${field}` || !addValue.trim()} onClick={() => addNarrative(field, addValue)} sx={{ color: 'primary.main' }}>
        {crudKey === `add:${field}` ? <CircularProgress size={16} /> : <CheckIcon fontSize="small" />}
      </IconButton>
      <IconButton size="small" onClick={() => { setAddingCat(null); setAddValue(''); }} sx={{ color: 'text.secondary' }}>
        <CloseIcon fontSize="small" />
      </IconButton>
    </Box>
  );

  // Full-panel "Mehr Details" view (in place of the run detail). Tab 0 = Themen
  // (per-category subsections of expandable Posten), Tab 1 = Quellen.
  const renderDetailsView = () => {
    const run = selectedRun;
    const a = run?.analysis || {};
    const evidence = run?.evidence || [];
    const catCount = (field) => (Array.isArray(a[field]) ? a[field].length : 0);
    const total = POSTEN_CATEGORIES.reduce((n, c) => n + catCount(c.field), 0);

    return (
      <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        {/* Header with back-to-overview */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexShrink: 0 }}>
          <IconButton size="small" onClick={closeDetails} title={t('are_back_overview')}><ArrowBackIcon fontSize="small" /></IconButton>
          <Box sx={{ width: 32, height: 32, borderRadius: '10px', bgcolor: hexA(ACCENT, 0.12), display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <ListAltIcon sx={{ fontSize: 18, color: 'primary.main' }} />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontWeight: 700, fontSize: 15, lineHeight: 1.1 }}>{t('are_more_details')}</Typography>
            <Typography sx={{ fontSize: 11.5, color: 'text.secondary' }}>{run?.company_name} {run?.ticker ? `(${run.ticker})` : ''}</Typography>
          </Box>
        </Box>

        {/* Themen / Quellen tabs */}
        <Tabs value={detailsTab} onChange={(e, v) => setDetailsTab(v)} variant="fullWidth"
          sx={{ minHeight: 44, borderBottom: 1, borderColor: 'divider', flexShrink: 0,
            '& .MuiTab-root': { minHeight: 44, textTransform: 'none', color: 'text.secondary', '&.Mui-selected': { color: 'primary.main' } },
            '& .MuiTabs-indicator': { bgcolor: 'primary.main' } }}>
          <Tab label={`${t('are_details_themen')} (${total})`} />
          <Tab label={`${t('are_sources')} (${evidence.length})`} />
        </Tabs>

        {/* Body */}
        <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', pt: 1.5, pr: 0.5 }}>
          {detailsTab === 0 ? (
            POSTEN_CATEGORIES.map((cat) => {
              const items = Array.isArray(a[cat.field]) ? a[cat.field] : [];
              return (
                <Box key={cat.field} sx={{ mb: 3 }}>
                  {/* Subsection header — clearly the header, entries sit beneath it */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                    <Box sx={{ width: '4px', alignSelf: 'stretch', minHeight: 30, borderRadius: '10px', bgcolor: 'primary.main', flexShrink: 0 }} />
                    <Box sx={{ width: 30, height: 30, borderRadius: '10px', bgcolor: hexA(ACCENT, 0.12), display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <cat.Icon sx={{ fontSize: 18, color: 'primary.main' }} />
                    </Box>
                    <Typography sx={{ fontSize: 14, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'primary.main' }}>
                      {t(cat.titleKey)}
                    </Typography>
                    <Box sx={{ flex: 1 }} />
                    {addingCat !== cat.field && (
                      <Button size="small" variant="text"
                        onClick={() => { setAddingCat(cat.field); setAddValue(''); }}
                        sx={{ textTransform: 'none', fontSize: 12, color: 'primary.main', flexShrink: 0, py: 0, minWidth: 0,
                          display: 'inline-flex', alignItems: 'center', gap: 0.4 }}>
                        <AddIcon sx={{ fontSize: 16 }} />
                        {t('are_narr_add')}
                      </Button>
                    )}
                  </Box>
                  {items.length === 0 && (
                    <Typography sx={{ color: 'text.secondary', fontSize: 12.5, mb: 1, pl: 1.5 }}>{t('are_modal_cat_empty')}</Typography>
                  )}
                  {/* pl 1.5 (12px) aligns the content with the header icon's left edge
                      (4px vertical line + 8px gap). */}
                  <Box sx={{ display: 'grid', gap: 0.85, pl: 1.5 }}>
                    {items.map((it) => renderPostenItem(it, cat))}
                  </Box>
                  {addingCat === cat.field && (
                    <Box sx={{ mt: 1, pl: 1.5 }}>{renderAddNarrative(cat.field)}</Box>
                  )}
                </Box>
              );
            })
          ) : (
            evidence.length === 0
              ? <Typography sx={{ color: 'text.secondary', fontSize: 13 }}>{t('are_modal_cat_empty')}</Typography>
              : evidence.map(renderSourceRow)
          )}
        </Box>
      </Box>
    );
  };

  // ── Render: run detail ──
  const renderRunDetail = () => {
    const run = selectedRun;
    const a = run.analysis || {};
    const ready = run.status === 'ready' && a && Object.keys(a).length > 0;
    return (
      <Box sx={{ flex: 1, overflowY: 'auto', pr: 0.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <IconButton size="small" onClick={() => { stopRunWatchers(); setRunning(false); setSelectedRun(null); fetchRuns(); }}><ArrowBackIcon fontSize="small" /></IconButton>
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontWeight: 600, fontSize: 15 }}>{run.company_name || ''} {run.ticker ? `(${run.ticker})` : ''}</Typography>
            {run.period_start && (
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>{t('are_period')}: {formatDate(run.period_start, language)} – {formatDate(run.period_end, language)}</Typography>
            )}
            {(run.intended_positioning || run.collection_config) && (
              <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5, flexWrap: 'wrap' }}>
                {run.intended_positioning && (
                  <Chip size="small" label={`${t('are_requested_leaning')}: ${t('are_' + run.intended_positioning)}`}
                    sx={{ height: 20, fontSize: 11, fontWeight: 600, bgcolor: hexA(ACCENT, 0.12), color: 'primary.main' }} />
                )}
                {run.collection_config && (
                  <Chip size="small" label={run.collection_config.include_screener ? t('are_screener_on') : t('are_screener_off')}
                    sx={{ height: 20, fontSize: 11, bgcolor: 'action.hover', color: 'text.secondary' }} />
                )}
              </Box>
            )}
          </Box>
          {!detailLoading && (
            <IconButton size="small" title={t('are_delete')} onClick={() => openDeleteDialog(run)} sx={{ color: 'text.secondary', '&:hover': { color: 'error.main' } }}>
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          )}
        </Box>

        {detailLoading && (() => {
          // When the report's original language differs from the UI language, the
          // first open triggers a one-time server-side translation → tell the user.
          const translating = run.language && run.language !== language;
          return (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5, py: 6 }}>
              <CircularProgress sx={{ color: 'primary.main' }} />
              <Typography variant="body2" sx={{ color: 'text.secondary', textAlign: 'center', px: 2 }}>
                {translating ? t('are_translating') : t('are_loading')}
              </Typography>
            </Box>
          );
        })()}

        {!detailLoading && running && !ready && renderRunningView()}
        {runError && <Typography sx={{ color: 'error.main', fontSize: 13, my: 1 }}>{runError}</Typography>}

        {!detailLoading && !running && !ready && run.status !== 'ready' && (
          <Box sx={{ textAlign: 'center', py: 3 }}>
            {run.status === 'collected'
              ? <Typography sx={{ color: 'text.secondary', mb: 2 }}>{t('are_no_evidence')}</Typography>
              : <Typography sx={{ color: 'text.secondary', mb: 2 }}>{t('are_run_not_started')}</Typography>}
            <Button variant="contained" startIcon={<PlayArrowIcon />} onClick={() => startRun(run.id)} sx={{ textTransform: 'none' }}>
              {t('are_run_start')}
            </Button>
          </Box>
        )}

        {ready && (() => {
          const positionTitle = t('are_positioning_question').replace(/:\s*$/, '');
          return (
          <>
            {/* 1. Bericht — the main reading surface (regenerate floats by its title) */}
            {renderSection('sec:bericht', t('are_bericht'), true, renderBericht(run), ArticleOutlinedIcon)}

            {/* 2. Position begründen */}
            {renderSection('sec:position', positionTitle, false, renderPosition(run), GavelIcon)}

            {/* 3. Mehr Details → Themen / Quellen page */}
            {renderDetailsEntry(a)}
          </>
          );
        })()}
      </Box>
    );
  };

  // ── Render: runs list ──
  const renderRunsList = () => (
    <Box sx={{ flex: 1, overflowY: 'auto' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>{t('are_runs_hint')}</Typography>
        <IconButton size="small" onClick={fetchRuns}><RefreshIcon fontSize="small" /></IconButton>
      </Box>
      {runsLoading && <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}><CircularProgress size={22} sx={{ color: 'primary.main' }} /></Box>}
      {!runsLoading && runs.length === 0 && (
        <Typography sx={{ color: 'text.secondary', fontSize: 13, textAlign: 'center', py: 3 }}>{t('are_runs_empty')}</Typography>
      )}
      {runs.map((r) => (
        <Box key={r.id} onClick={() => {
            stopRunWatchers(); setRunning(false);
            // A still-running analysis resumes its live view (and re-kicks /run if
            // it had silently stalled); a finished one just opens.
            if (IN_PROGRESS.includes(r.status)) { setSelectedRun(r); startRun(r.id); }
            else { openRun(r.id, { instant: true, sourceLang: r.status === 'ready' ? r.language : null }); }
          }}
          sx={{ p: 1.5, mb: 1, borderRadius: '10px', border: 1, borderColor: 'divider', bgcolor: 'background.paper',
            cursor: 'pointer', '&:hover': { borderColor: 'primary.main', bgcolor: 'action.hover' } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
            <Typography sx={{ fontWeight: 600, fontSize: 14, flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {r.company_name} {r.ticker ? `(${r.ticker})` : ''}
            </Typography>
            <Chip size="small" label={t('are_status_' + r.status, r.status)}
              color={r.status === 'ready' ? 'success' : r.status === 'error' ? 'error' : 'default'}
              variant={r.status === 'ready' ? 'filled' : 'outlined'} sx={{ fontSize: 11, height: 20 }} />
            <IconButton size="small" title={t('are_duplicate')} onClick={(e) => duplicateRun(r, e)}
              sx={{ color: 'text.secondary', '&:hover': { color: 'primary.main' } }}>
              <ContentCopyIcon sx={{ fontSize: 16 }} />
            </IconButton>
            <IconButton size="small" title={t('are_delete')} onClick={(e) => openDeleteDialog(r, e)}
              sx={{ color: 'text.secondary', '&:hover': { color: 'error.main' } }}>
              <DeleteOutlineIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Box>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {formatDateTime(r.created_at, language)}
            {r.analysis?.overall_assessment ? ` · ${t('are_overall_' + r.analysis.overall_assessment)}` : ''}
          </Typography>
        </Box>
      ))}
    </Box>
  );

  // ── Speech-to-text: record mic audio, transcribe, drop text into input ──
  const handleRecordClick = async (target = 'assistant') => {
    if (isRecording) {
      // Capture the actual rate before closing the context; the WAV header must
      // match the native rate the ScriptProcessor recorded at.
      const sampleRate = audioContextRef.current ? audioContextRef.current.sampleRate : 44100;

      if (processorRef.current && mediaStreamSourceRef.current) {
        mediaStreamSourceRef.current.disconnect();
        processorRef.current.disconnect();
      }
      if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
        audioContextRef.current.close();
      }
      if (mediaStreamSourceRef.current && mediaStreamSourceRef.current.mediaStream) {
        mediaStreamSourceRef.current.mediaStream.getTracks().forEach((track) => track.stop());
      }

      setIsRecording(false);
      processAudioData(audioBufferRef.current, sampleRate);
    } else {
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        alert('Your browser does not support audio recording.');
        return;
      }

      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        const audioContext = new (window.AudioContext || window.webkitAudioContext)();
        audioContextRef.current = audioContext;
        mediaStreamSourceRef.current = audioContext.createMediaStreamSource(stream);
        processorRef.current = audioContext.createScriptProcessor(4096, 1, 1);

        mediaStreamSourceRef.current.connect(processorRef.current);
        processorRef.current.connect(audioContext.destination);

        audioBufferRef.current = [];
        sttTargetRef.current = target;
        processorRef.current.onaudioprocess = (e) => {
          const inputBuffer = e.inputBuffer.getChannelData(0);
          audioBufferRef.current.push(new Float32Array(inputBuffer));
        };

        setIsRecording(true);
      } catch (error) {
        console.error('Error accessing microphone:', error);
        alert('Could not access your microphone. Please check permissions.');
      }
    }
  };

  const processAudioData = async (buffer, sampleRate = 44100) => {
    setIsAudioLoading(true);

    const wavBlob = encodeWAV(buffer, sampleRate);
    const reader = new FileReader();
    reader.readAsDataURL(wavBlob);

    reader.onloadend = async () => {
      const base64data = reader.result.split(',')[1];
      try {
        const response = await fetch(`${API_BASE_URL}/api/translate_audio_to_text`, {
          method: 'POST',
          headers: getAuthHeaders({ 'Content-Type': 'application/json' }),
          body: JSON.stringify({ audio_file: base64data }),
        });
        if (!response.ok) throw new Error('Failed to process audio');

        const data = await response.json();
        if (data && data.user_input) {
          const setTargetInput = sttTargetRef.current === 'free' ? setFreeInput : setInput;
          setTargetInput((prev) => (prev ? `${prev} ${data.user_input}` : data.user_input));
        } else {
          console.error('Invalid response from API');
        }
      } catch (error) {
        console.error('Error processing audio:', error);
      } finally {
        setIsAudioLoading(false);
      }
    };
  };

  // ── Render: assistant chat ──
  const renderAssistant = () => (
    <>
      <Box sx={{ position: 'relative', flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      <Box ref={chatScrollRef} onScroll={handleScroll} sx={{ position: 'relative', flex: 1, overflowY: 'auto', pt: 1 }}>
        {messages.map((m, i) => {
          const isUser = m.sender === 'user';
          const isLast = i === messages.length - 1;
          const isLoadingBubble = !isUser && !m.text && sending && isLast;
          // The Word button only makes sense on a finished answer: not on the
          // greeting, and not while this very message is still streaming.
          const showWordExport = !isUser && !!m.text && !m.welcome && !(sending && isLast);
          return (
            <Box key={i} className={isUser ? 'chat-user-msg' : undefined} sx={chatRowSx(isUser)}>
              {!isUser && <BotAvatar />}
              <Box sx={isLoadingBubble
                ? { px: 0.5, bgcolor: 'transparent', color: 'text.primary', display: 'flex', alignItems: 'center', minHeight: `${FIRST_LINE_SLOT_PX}px` }
                : { ...chatBubbleSx(isUser), whiteSpace: isUser ? 'pre-wrap' : 'normal' }}>
                {isUser ? (
                  m.text
                ) : m.text ? (
                  <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw]} components={chatMarkdownComponents}>{alignNumericColumns(m.text)}</ReactMarkdown>
                ) : (sending && isLast ? <CircularProgress size={22} thickness={4} sx={{ color: 'primary.main' }} /> : '')}
                {/* Export the exchange as Word — same button, position and
                    endpoint as Assistant Bro; it always exports the whole
                    conversation, not just this message. */}
                {showWordExport && (
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                    <InfoTooltip title={t('export_chat_history_to_word')}>
                      <IconButton size="small" onClick={exportAssistantWord} sx={{ color: 'text.secondary' }}>
                        <DescriptionIcon fontSize="small" />
                      </IconButton>
                    </InfoTooltip>
                  </Box>
                )}
              </Box>
            </Box>
          );
        })}
        {/* Growable spacer + scroll anchor: let the just-sent user message be
            scrolled to the top of the viewport. */}
        <div ref={bottomSpacerRef} style={{ flexShrink: 0 }} />
        <div ref={messagesEndRef} />
      </Box>
        {/* "Scroll to latest" arrow — shown when not at the bottom. */}
        <ScrollToBottomButton show={showScrollDown} onClick={scrollToBottom} />
      </Box>
      <Box sx={{ pt: 1, borderTop: 1, borderColor: 'divider', display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <TextField
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
          placeholder={isAudioLoading ? '' : t('are_assistant_placeholder')}
          disabled={sending || isAudioLoading}
          multiline maxRows={4} size="small" fullWidth
          InputProps={{ startAdornment: isAudioLoading ? (<InputAdornment position="start"><CircularProgress size={18} sx={{ color: 'primary.main' }} /></InputAdornment>) : null }}
          sx={{ '& .MuiOutlinedInput-root': { bgcolor: 'background.paper', fontSize: 14 }, '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderWidth: 1 } }}
        />
        <InfoTooltip title={t('send_tooltip')}>
          <span style={{ display: 'inline-flex' }}>
            <IconButton onClick={sendMessage} disabled={sending || isAudioLoading || isRecording || !input.trim()} sx={{ color: 'primary.main' }}><SendIcon fontSize="small" /></IconButton>
          </span>
        </InfoTooltip>
        <InfoTooltip title={isRecording ? t('stt_stop_tooltip') : t('stt_tooltip')}>
          <span style={{ display: 'inline-flex' }}>
            <IconButton onClick={() => handleRecordClick('assistant')} disabled={sending || isAudioLoading} aria-label="Speech to text" sx={{ color: isRecording ? 'error.main' : 'text.secondary' }}>
              {isRecording ? <FiberManualRecordIcon fontSize="small" /> : <MicIcon fontSize="small" />}
            </IconButton>
          </span>
        </InfoTooltip>
        <InfoTooltip title={t('voice_tooltip')}>
          <span style={{ display: 'inline-flex' }}>
            <IconButton onClick={() => setVoiceOpen(true)} disabled={sending || isAudioLoading || isRecording} aria-label="AI Voice Agent" sx={{ color: 'text.secondary' }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 3V21M8 8V16M4 10V14M16 8V16M20 10V14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              </svg>
            </IconButton>
          </span>
        </InfoTooltip>
      </Box>
    </>
  );

  // ── Render: Not Controlled Chat ──
  // Same bubble/scroll mechanics as the assistant, minus the ARE voice agent and
  // plus the two things this chat has of its own: a "searching the web" strip
  // and the source chips under an answer that used the web_search tool.
  const renderFreeChat = () => (
    <>
      <Box sx={{ position: 'relative', flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        <Box ref={freeScrollRef} onScroll={freeHandleScroll} sx={{ position: 'relative', flex: 1, overflowY: 'auto', pt: 1 }}>
          {freeMessages.map((m, i) => {
            const isUser = m.sender === 'user';
            const isLast = i === freeMessages.length - 1;
            const isLoadingBubble = !isUser && !m.text && freeSending && isLast;
            // The Word button only makes sense on a finished answer: not on the
            // greeting, and not while this very message is still streaming.
            const showWordExport = !isUser && !!m.text && !m.welcome && !(freeSending && isLast);
            return (
              <Box key={i} className={isUser ? 'chat-user-msg' : undefined} sx={chatRowSx(isUser)}>
                {!isUser && <BotAvatar />}
                <Box sx={isLoadingBubble
                  ? { px: 0.5, bgcolor: 'transparent', color: 'text.primary', display: 'flex', alignItems: 'center', minHeight: `${FIRST_LINE_SLOT_PX}px` }
                  : { ...chatBubbleSx(isUser), whiteSpace: isUser ? 'pre-wrap' : 'normal' }}>
                  {isUser ? (
                    m.text
                  ) : m.text ? (
                    <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw]} components={chatMarkdownComponents}>{alignNumericColumns(m.text)}</ReactMarkdown>
                  ) : (freeSending && isLast ? <CircularProgress size={22} thickness={4} sx={{ color: 'primary.main' }} /> : '')}
                  {/* Export the exchange as Word — same button, position and
                      endpoint as Assistant Bro; it always exports the whole
                      conversation, not just this message. */}
                  {showWordExport && (
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                      <InfoTooltip title={t('export_chat_history_to_word')}>
                        <IconButton size="small" onClick={exportFreeWord} sx={{ color: 'text.secondary' }}>
                          <DescriptionIcon fontSize="small" />
                        </IconButton>
                      </InfoTooltip>
                    </Box>
                  )}
                </Box>
                {/* Sources behind the answer — the url citations the web_search
                    tool returned, de-duplicated server-side. Indented to the
                    bubble's left edge (the avatar slot is 40px incl. its gap). */}
                {!isUser && m.sources && m.sources.length > 0 && (
                  <Box sx={{ width: '100%', pl: '40px', mt: -0.25, mb: 0.75 }}>
                    <Typography sx={{ fontSize: 11, color: 'text.disabled', mb: 0.5 }}>{t('are_sources')}</Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {m.sources.map((src, si) => (
                        <Chip
                          key={src.url || si}
                          component="a"
                          href={src.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          clickable
                          size="small"
                          icon={<OpenInNewIcon sx={{ fontSize: 13 }} />}
                          label={`${si + 1}. ${src.domain || src.title || src.url}`}
                          title={src.title || src.url}
                          sx={{
                            maxWidth: '100%', height: 22, borderRadius: '11px',
                            border: 1, borderColor: 'divider', bgcolor: 'transparent',
                            color: 'text.secondary',
                            '& .MuiChip-label': { fontSize: 11, px: 0.75 },
                            '&:hover': { borderColor: 'primary.main', color: 'primary.main', bgcolor: 'action.hover' },
                          }}
                        />
                      ))}
                    </Box>
                  </Box>
                )}
              </Box>
            );
          })}
          {/* Growable spacer + scroll anchor — see renderAssistant. */}
          <div ref={freeBottomSpacerRef} style={{ flexShrink: 0 }} />
          <div ref={freeMessagesEndRef} />
        </Box>
        <ScrollToBottomButton show={freeShowScrollDown} onClick={freeScrollToBottom} />
      </Box>
      {/* Live web-search indicator (the model called the web_search tool). */}
      <Collapse in={freeSearching}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, pl: '40px', pb: 0.5 }}>
          <TravelExploreIcon sx={{ fontSize: 15, color: 'primary.main' }} />
          <Typography sx={{ fontSize: 11.5, color: 'text.secondary' }}>{t('are_free_searching')}</Typography>
        </Box>
      </Collapse>
      <Box sx={{ pt: 1, borderTop: 1, borderColor: 'divider', display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <TextField
          value={freeInput}
          onChange={(e) => setFreeInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendFreeMessage(); } }}
          placeholder={isAudioLoading ? '' : t('are_free_placeholder')}
          disabled={freeSending || isAudioLoading}
          multiline maxRows={6} size="small" fullWidth
          InputProps={{ startAdornment: isAudioLoading ? (<InputAdornment position="start"><CircularProgress size={18} sx={{ color: 'primary.main' }} /></InputAdornment>) : null }}
          sx={{ '& .MuiOutlinedInput-root': { bgcolor: 'background.paper', fontSize: 14 }, '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderWidth: 1 } }}
        />
        {/* While a turn streams, Send becomes Stop — closing the stream keeps
            the partial answer (the backend persists it too). */}
        {freeSending ? (
          <InfoTooltip title={t('are_free_stop')}>
            <span style={{ display: 'inline-flex' }}>
              <IconButton onClick={stopFree} aria-label={t('are_free_stop')} sx={{ color: 'error.main' }}><StopCircleIcon fontSize="small" /></IconButton>
            </span>
          </InfoTooltip>
        ) : (
          <InfoTooltip title={t('send_tooltip')}>
            <span style={{ display: 'inline-flex' }}>
              <IconButton onClick={sendFreeMessage} disabled={isAudioLoading || isRecording || !freeInput.trim()} sx={{ color: 'primary.main' }}><SendIcon fontSize="small" /></IconButton>
            </span>
          </InfoTooltip>
        )}
        <InfoTooltip title={isRecording ? t('stt_stop_tooltip') : t('stt_tooltip')}>
          <span style={{ display: 'inline-flex' }}>
            <IconButton onClick={() => handleRecordClick('free')} disabled={freeSending || isAudioLoading} aria-label="Speech to text" sx={{ color: isRecording ? 'error.main' : 'text.secondary' }}>
              {isRecording ? <FiberManualRecordIcon fontSize="small" /> : <MicIcon fontSize="small" />}
            </IconButton>
          </span>
        </InfoTooltip>
        <InfoTooltip title={t('voice_tooltip')}>
          <span style={{ display: 'inline-flex' }}>
            <IconButton onClick={() => setFreeVoiceOpen(true)} disabled={freeSending || isAudioLoading || isRecording} aria-label="AI Voice Agent" sx={{ color: 'text.secondary' }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 3V21M8 8V16M4 10V14M16 8V16M20 10V14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              </svg>
            </IconButton>
          </span>
        </InfoTooltip>
      </Box>
    </>
  );

  // Which "screen" is showing, and whether we navigated forward (deeper) or back
  // — drives the slide direction. The ref tracks the previous screen across
  // renders without forcing an extra render.
  const screen = tab === 0 ? 'assistant'
    : tab === 2 ? 'free'
    : (selectedRun ? (detailsOpen ? 'details' : 'detail') : 'list');
  // "Clear chat" acts on whichever chat tab is showing; the Analysen tab has
  // no transcript, so the button is hidden there.
  const chatTabMessages = tab === 0 ? messages : tab === 2 ? freeMessages : null;
  const chatTabBusy = (tab === 2 ? freeSending : sending) || isAudioLoading || isRecording;
  // There is nothing to clear only when the transcript is still just the welcome
  // AND that welcome is already in the language the advisor is using. After a
  // language switch the button lights up again so the greeting can be refreshed.
  const chatTabWelcomeLang = tab === 2 ? freeWelcomeLang : welcomeLang;
  const chatTabEmpty = !!chatTabMessages && chatTabMessages.length <= 1
    && chatTabWelcomeLang === language;
  const navDir = SCREEN_DEPTH[screen] < SCREEN_DEPTH[prevScreenRef.current] ? 'back' : 'fwd';
  useEffect(() => { prevScreenRef.current = screen; }, [screen]);

  return (
    <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      {/* Header: icon + title (left), Clear Chat + Close (right), tabs below. */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pb: 1 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 32,
            height: 32,
            borderRadius: '8px',
            bgcolor: 'rgba(25, 208, 239, 0.08)',
            color: 'primary.main',
            '& .MuiSvgIcon-root': { fontSize: 18 },
          }}
        >
          <AutoAwesomeIcon />
        </Box>
        <Typography sx={{ fontWeight: 400, fontSize: '15px', letterSpacing: '0.02em', flex: 1 }}>
          {t('advisor_reasoning')}
        </Typography>
        {chatTabMessages && (
          <InfoTooltip title={chatTabEmpty ? t('no_chat_history') : ''}>
            <span>
              <Button
                onClick={tab === 2 ? newFreeChat : newChat}
                disabled={chatTabBusy || chatTabEmpty}
                size="small"
                startIcon={<DeleteOutlineIcon />}
                sx={{ ...modalHeaderBtnSx, px: 1.5, py: 0.5, fontSize: '13px', fontWeight: 600 }}
              >
                {t('clear_chat')}
              </Button>
            </span>
          </InfoTooltip>
        )}
        {onClose && (
          <IconButton
            onClick={() => onClose()}
            size="small"
            aria-label={t('close', { defaultValue: 'Close' })}
            sx={{ ...modalHeaderBtnSx, p: 0.75 }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        )}
      </Box>
      <Tabs value={tab} onChange={(e, v) => setTab(v)} variant="fullWidth"
        sx={{ minHeight: 40, borderBottom: '1px solid rgba(25, 208, 239, 0.35)', '& .MuiTab-root': { minHeight: 40, textTransform: 'none', minWidth: 0, px: 0.5, fontSize: { xs: 11.5, sm: 13 }, color: 'text.secondary', '&.Mui-selected': { color: 'primary.main' } } }}>
        <Tab label={t('are_tab_assistant')} />
        <Tab label={t('are_tab_runs')} />
        <Tab label={t('are_tab_free')} />
      </Tabs>
      {/* One keyed wrapper per "screen" so navigation cross-animates smoothly
          (list → run detail → Mehr Details and back, plus the tab switch). The
          incoming screen slides in from the right when going deeper, from the
          left when going back. `screen`/`navDir` are computed above the return. */}
      <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', pt: 1, overflow: 'hidden' }}>
        <Box key={screen} className={`are-screen are-screen-${navDir}`}
          sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
          {screen === 'assistant' && renderAssistant()}
          {screen === 'free' && renderFreeChat()}
          {screen === 'list' && renderRunsList()}
          {screen === 'detail' && renderRunDetail()}
          {screen === 'details' && renderDetailsView()}
        </Box>
      </Box>
      {/* Persistent disclaimer — pinned to the bottom of every ARE tab/sub-mask.
         No divider line; the top padding mirrors the modal's own bottom padding
         (p:{xs:1.5,md:2}) so the text sits vertically centred in the gap under
         the input row. */}
      <AreDisclaimer sx={{ py: 0, pt: { xs: 1.5, md: 2 } }}
        textKey={screen === 'free' ? 'are_free_disclaimer' : 'are_disclaimer'} />
      {voiceOpen && (
        <AdvisorVoiceDialog
          language={language}
          onClose={() => setVoiceOpen(false)}
          onRunStarted={(d) => { setVoiceOpen(false); handleRunStarted(d); }}
        />
      )}
      {/* Speech-to-speech for the Not Controlled Chat. Same overlay as the
          assistant's, pointed at the free-chat turn handler and handed THIS
          tab's session id — so a spoken question and a typed follow-up are one
          conversation, on screen and in chat_history. */}
      {freeVoiceOpen && (
        <AdvisorVoiceDialog
          language={language}
          endpoint="/api/are/free-chat/voice"
          greetingKey="are_free_voice_greeting"
          searchingKey="are_free_searching"
          disclaimerKey="are_free_disclaimer"
          sessionId={freeSessionIdRef.current}
          onTurn={appendFreeVoiceTurn}
          onClose={() => setFreeVoiceOpen(false)}
        />
      )}

      {/* Delete-confirmation dialog — same styling as the Assistant-Lion VIP delete. */}
      <Dialog
        open={deleteDialog.open}
        onClose={closeOnlyViaButton(closeDeleteDialog)}
        maxWidth="xs"
        fullWidth
      >
        <Box sx={{ px: 3, pt: 2 }}>
          <ModalHeader icon={<DeleteOutlineIcon />} title={t('are_delete_title')} onClose={closeDeleteDialog} />
        </Box>
        <DialogContent>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: deleteDialog.run ? 2 : 0 }}>
            {t('are_delete_confirm')}
          </Typography>
          {deleteDialog.run && (
            <Box sx={{ p: 1.5, borderRadius: 1, bgcolor: 'rgba(255, 255, 255, 0.06)', border: 1, borderColor: 'divider' }}>
              <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 600 }}>
                {deleteDialog.run.company_name}{deleteDialog.run.ticker ? ` (${deleteDialog.run.ticker})` : ''}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeDeleteDialog} color="inherit" disabled={deleting}>
            {t('are_cancel')}
          </Button>
          <Button
            onClick={confirmDeleteRun}
            variant="contained"
            color="error"
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={16} color="inherit" /> : <DeleteOutlineIcon />}
          >
            {t('are_delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default ExpandableAdvisorReasoning;
